import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';

const ManufacturingHero = () => {
  const [activeLocation, setActiveLocation] = useState('Ahmedabad');
  const contentRef = useRef(null);
  const titleRef = useRef(null);
  
  const locations = [
    { id: 'ahmedabad', name: 'Ahmedabad' },
    { id: 'mumbai', name: 'Mumbai' },
    { id: 'delhi', name: 'Delhi' }
  ];

  const handleLocationChange = (locationName) => {
    setActiveLocation(locationName);
  };
  
  useEffect(() => {
    // Simple fade-in animation for content
    if (contentRef.current) {
      gsap.fromTo(contentRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.4, ease: "power1.out" }
      );
    }
  }, [activeLocation]);
  
  useEffect(() => {
    // Initial animation for title
    if (titleRef.current) {
      gsap.fromTo(titleRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
      );
    }
  }, []);

  return (
    <section className="py-8 md:pt-[80px] md:pb-[62px] bg-[#F9F9F9]">
      <div className="container-custom px-[20px]">
        {/* Breadcrumb Navigation */}
        <div className="mb-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 opacity-50">
              <li>
                <Link
                  to="/"
                  className="text-gray-600 hover:text-gray-800 text-sm md:text-base inter-font-medium"
                >
                  Home
                </Link>
              </li>
              <li className="flex items-center">
                <span className="text-gray-400 mx-2">/</span>
                <span className="text-gray-800 text-sm md:text-base font-medium">
                  Manufacturing Capacity
                </span>
              </li>
            </ol>
          </nav>
        </div>

        {/* Page Title */}
        <div ref={titleRef} className="mt-4 mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-[40px] text-gray-900 inter-font-medium">
            Agastya Agro's Manufacturing Capacity
          </h1>
        </div>
        
        {/* Location Tabs - Styled like in the image */}
        <div className="flex flex-wrap gap-3 mt-8">
          {locations.map((location) => (
            <button
              key={location.id}
              onClick={() => handleLocationChange(location.name)}
              className={`py-2 px-6 rounded-full border transition-all duration-200 text-sm md:text-base ${
                activeLocation === location.name
                  ? 'bg-gray-100 text-gray-800 border-gray-300 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {location.name}
            </button>
          ))}
        </div>
        
        {/* Location Content with animation */}
        {/* <div ref={contentRef} className="max-w-5xl mx-auto mt-8 transition-all duration-300">
          {activeLocation === 'Ahmedabad' && (
            <div>
              <div className="mb-8 border-b border-gray-200 pb-4">
                <h3 className="text-2xl font-semibold mb-4 font-[LTWave]">Ahmedabad Facility</h3>
                <p className="mb-4 text-gray-700 leading-relaxed">
                  Our state-of-the-art manufacturing facility in Ahmedabad spans over 50,000 square feet and is equipped with the latest technology for precision manufacturing.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="text-xl font-semibold mb-4 font-[LTWave]">Production Capacity</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Annual output: 10,000 units</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>24/7 operation capability</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>ISO 9001:2015 certified facility</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="text-xl font-semibold mb-4 text-gray-800">Specializations</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Precision engineering</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Advanced automation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Quality control systems</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {activeLocation === 'Mumbai' && (
            <div>
              <div className="mb-8 border-b border-gray-200 pb-4">
                <h3 className="text-2xl font-semibold mb-4 font-[LTWave]">Mumbai Facility</h3>
                <p className="mb-4 text-gray-700 leading-relaxed">
                  Our Mumbai facility focuses on specialized manufacturing with cutting-edge equipment and expert technicians.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="text-xl font-semibold mb-4 text-gray-800">Production Capacity</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Annual output: 8,000 units</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Specialized in high-precision components</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>ISO 14001 environmental certification</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="text-xl font-semibold mb-4 text-gray-800">Specializations</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Advanced materials processing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Custom solutions development</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Rapid prototyping capabilities</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {activeLocation === 'Delhi' && (
            <div>
              <div className="mb-8 border-b border-gray-200 pb-4">
                <h3 className="text-2xl font-semibold mb-4 font-[LTWave]">Delhi Facility</h3>
                <p className="mb-4 text-gray-700 leading-relaxed">
                  Our Delhi manufacturing center serves as our innovation hub, focusing on research and development alongside production.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="text-xl font-semibold mb-4 text-gray-800">Production Capacity</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Annual output: 6,000 units</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>R&D integrated production facility</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>IATF 16949 automotive quality certification</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <h4 className="text-xl font-semibold mb-4 text-gray-800">Specializations</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Next-generation product development</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Smart manufacturing processes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-gray-400">•</span>
                      <span>Sustainable production methods</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div> */}
      </div>
    </section>
  );
};

export default ManufacturingHero;
